import React, { useCallback, useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  DataListTable,
  ErrorState,
  LoadingState,
  PaginationControls,
  SectionCard,
  formatDateTime,
  formatNumber,
} from '../../components/superadmin/SuperAdminUI';

const DEFAULT_PAGINATION = { page: 1, pageSize: 20, total: 0, totalPages: 0 };

const ShopClients = () => {
  const { shop, shopId } = useOutletContext();
  const { getShopClients } = useSuperadminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const loadClients = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await getShopClients(shopId, {
        page,
        pageSize: pagination.pageSize,
      });

      if (!response?.ok) {
        throw new Error('Failed to load clients');
      }

      setClients(response.data || []);
      setPagination(response.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [getShopClients, pagination.pageSize, shopId]);

  useEffect(() => {
    loadClients(1);
  }, [loadClients]);

  return (
    <div className="space-y-6">
      {error ? <ErrorState error={error} onRetry={() => loadClients(pagination.page)} /> : null}

      <SectionCard
        title="Clients"
        subtitle={`Most recent client records linked to ${shop?.name || `Shop #${shopId}`}`}
        icon={<Users className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        <div className="space-y-4">
          {loading ? (
            <LoadingState lines={6} />
          ) : (
            <DataListTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'bookings', label: 'Bookings', render: (row) => formatNumber(row.bookings?.total) },
                { key: 'last_hairstyle_analysis', label: 'Last AI analysis', render: (row) => formatDateTime(row.last_hairstyle_analysis) },
              ]}
              rows={clients}
              emptyTitle="No clients found"
              emptyDescription="No active client records were returned for this shop."
            />
          )}

          <PaginationControls pagination={pagination} onPageChange={loadClients} loading={loading} />
        </div>
      </SectionCard>
    </div>
  );
};

export default ShopClients;
